import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Phone, Package } from "lucide-react";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useStoreBase } from "@/lib/store";

function when(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function addressLines(addr = {}) {
  const pin = addr.pincode || addr.zipcode || "";
  const city = [addr.city, addr.state, pin].filter(Boolean).join(", ");
  return [addr.line1, addr.line2, city].map((s) => String(s || "").trim()).filter(Boolean);
}

function mapsDestination(addr = {}) {
  return addressLines(addr).join(", ");
}

/** Web directions link with the customer address as the destination. */
export function mapsWebUrl(destination) {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}&travelmode=driving`;
}

function isAndroid() {
  return /Android/i.test(navigator.userAgent || "");
}

function isIOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent || "");
}

/** Opens Google Maps to this address. On a phone, try the app first; if it is not installed, open the web link. */
export function openCustomerMap(destination) {
  const dest = String(destination || "").trim();
  if (!dest) return;
  const web = mapsWebUrl(dest);
  const encoded = encodeURIComponent(dest);
  if (isAndroid()) {
    window.location.href = `intent://maps.google.com/maps?daddr=${encoded}&directionsmode=driving#Intent;scheme=https;package=com.google.android.apps.maps;S.browser_fallback_url=${encodeURIComponent(web)};end`;
    return;
  }
  if (isIOS()) {
    const started = Date.now();
    const timer = window.setTimeout(() => {
      if (document.hidden || Date.now() - started > 1600) return;
      window.location.href = web;
    }, 800);
    document.addEventListener("visibilitychange", () => window.clearTimeout(timer), { once: true });
    window.location.href = `comgooglemaps://?daddr=${encoded}&directionsmode=driving`;
    return;
  }
  window.open(web, "_blank", "noopener,noreferrer");
}

function statusLabel(o) {
  const s = o.ondemand?.status || o.status || "";
  if (s === "nearby") return "Nearby";
  if (s === "delivered") return "Delivered";
  if (s === "accepted") return "Accepted";
  if (s === "prep") return "New";
  return s ? s.replace(/_/g, " ") : "Open";
}

export default function DeliverPanel() {
  const nav = useNavigate();
  const { base } = useStoreBase();
  const home = `${base || ""}/deliver`;
  const [me, setMe] = useState(null);
  const [incoming, setIncoming] = useState([]);
  const [accepted, setAccepted] = useState([]);
  const [delivered, setDelivered] = useState([]);
  const [rejected, setRejected] = useState([]);

  const load = async () => {
    try {
      const { data: m } = await api.get("/deliver/me");
      setMe(m);
      const { data } = await api.get("/deliver/orders");
      setIncoming(data.incoming || []);
      setAccepted(data.accepted || []);
      setDelivered(data.delivered || []);
      setRejected(data.rejected || []);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        nav(home, { replace: true });
        return;
      }
      toast.error(extractErr(e));
    }
  };
  useEffect(() => { load(); const t = setInterval(load, 12000); return () => clearInterval(t); }, []);

  const act = async (id, path) => {
    if (path === "reject" && !window.confirm("Reject this order? It goes back so another rider can take it.")) return;
    try {
      await api.post(`/deliver/orders/${id}/${path}`);
      toast.success(path === "delivered" ? "Marked delivered" : path === "reject" ? "Order rejected" : "Updated");
      await load();
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  const logout = async () => {
    await api.post("/deliver/logout");
    nav(home, { replace: true });
  };

  const OrderCard = ({ o, actions, tone = "open" }) => {
    const addr = o.address || {};
    const lines = addressLines(addr);
    const destination = mapsDestination(addr);
    const phone = String(addr.phone || "").trim();
    const name = o.customer_name || addr.name || "Customer";
    const items = Array.isArray(o.items) ? o.items : [];
    const symbol = o.currency_symbol || "₹";
    const cod = o.payment_method === "cod";
    return (
      <article className="rounded-2xl bg-white border border-stone-200 shadow-sm overflow-hidden" data-testid={`deliver-order-${o.id}`}>
        <div className={`px-4 py-2.5 flex items-center justify-between gap-2 text-xs font-bold uppercase tracking-wide ${tone === "done" ? "bg-emerald-50 text-emerald-800" : tone === "rejected" ? "bg-rose-50 text-rose-800" : "bg-stone-50 text-stone-600"}`}>
          <span>{o.order_number}</span>
          <span>{statusLabel(o)}</span>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-stone-400 font-semibold">Customer</div>
            <div className="text-lg font-extrabold text-stone-900 leading-tight" data-testid={`deliver-customer-${o.id}`}>{name}</div>
            {phone ? (
              <a href={`tel:${phone}`} className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-stone-800" data-testid={`deliver-call-${o.id}`}>
                <Phone className="w-3.5 h-3.5" /> {phone}
              </a>
            ) : null}
          </div>
          <div className="rounded-xl bg-stone-50 border border-stone-100 p-3" data-testid={`deliver-address-${o.id}`}>
            <div className="flex items-start gap-2">
              {destination ? (
                <button
                  type="button"
                  onClick={() => openCustomerMap(destination)}
                  className="shrink-0 inline-flex items-center gap-1 h-8 px-2.5 rounded-full bg-[#0C831F] text-white text-xs font-bold"
                  data-testid={`deliver-map-${o.id}`}
                >
                  <MapPin className="w-3.5 h-3.5" /> View on map
                </button>
              ) : (
                <MapPin className="w-4 h-4 mt-0.5 text-stone-500 shrink-0" />
              )}
              <div className="min-w-0 text-sm text-stone-800 leading-snug">
                {lines.length ? lines.map((line) => <div key={line}>{line}</div>) : <div>Address not added</div>}
              </div>
            </div>
          </div>
          {items.length ? (
            <div className="space-y-1">
              {items.map((it, i) => (
                <div key={`${it.name}-${i}`} className="flex items-start gap-2 text-sm text-stone-700">
                  <Package className="w-3.5 h-3.5 mt-0.5 text-stone-400 shrink-0" />
                  <span>{it.qty || 1} × {it.name}{it.variant_name ? ` · ${it.variant_name}` : ""}</span>
                </div>
              ))}
            </div>
          ) : null}
          {o.notes ? <p className="text-sm text-stone-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">{o.notes}</p> : null}
          <div className="flex items-center justify-between gap-2 pt-1">
            <div>
              <div className="text-base font-extrabold text-stone-900">{symbol}{Number(o.total || 0).toLocaleString("en-IN")}</div>
              <div className="text-xs text-stone-500">{cod ? "Cash on delivery" : (o.payment_method || "Paid")}{o.created_at ? ` · ${when(o.created_at)}` : ""}</div>
            </div>
            {o.ondemand?.delivered_at ? <div className="text-xs font-semibold text-emerald-700">Delivered {when(o.ondemand.delivered_at)}</div> : null}
          </div>
          {actions ? <div className="flex flex-wrap gap-2 pt-1">{actions}</div> : null}
        </div>
      </article>
    );
  };

  const stats = [
    { label: "Incoming", n: incoming.length },
    { label: "Active", n: accepted.length },
    { label: "Delivered", n: delivered.length },
    { label: "Rejected", n: rejected.length },
  ];

  return (
    <div className="min-h-screen bg-[#f3f4f6]" data-testid="deliver-panel">
      <header className="bg-[#0C831F] text-white">
        <div className="max-w-3xl mx-auto px-4 pt-4 pb-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-white/70">Delivery panel</div>
              <div className="text-xl font-extrabold">{me?.name || "Rider"}</div>
            </div>
            <Button variant="outline" className="text-white border-white/40 bg-white/10 hover:bg-white/20" onClick={logout}>Sign out</Button>
          </div>
          <div className="mt-4 grid grid-cols-4 gap-2">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl bg-white/15 px-2 py-2 text-center">
                <div className="text-lg font-extrabold leading-none">{s.n}</div>
                <div className="mt-1 text-[10px] uppercase tracking-wide text-white/80">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </header>
      <div className="max-w-3xl mx-auto p-4 space-y-8 pb-16">
        <section>
          <h2 className="font-extrabold text-lg mb-3">Incoming</h2>
          <div className="space-y-3">
            {incoming.map((o) => (
              <OrderCard
                key={o.id}
                o={o}
                actions={(
                  <>
                    <Button size="sm" className="bg-[#0C831F] hover:bg-[#0a6e1a]" onClick={() => act(o.id, "accept")} data-testid={`deliver-accept-${o.id}`}>Accept</Button>
                    <Button size="sm" variant="outline" onClick={() => act(o.id, "reject")} data-testid={`deliver-reject-${o.id}`}>Reject</Button>
                  </>
                )}
              />
            ))}
            {!incoming.length ? <p className="text-sm text-stone-500">No open orders.</p> : null}
          </div>
        </section>
        <section>
          <h2 className="font-extrabold text-lg mb-3">My deliveries</h2>
          <div className="space-y-3">
            {accepted.map((o) => (
              <OrderCard
                key={o.id}
                o={o}
                actions={(
                  <>
                    {o.ondemand?.status !== "nearby" && o.ondemand?.status !== "delivered" ? (
                      <Button size="sm" variant="outline" onClick={() => act(o.id, "nearby")} data-testid={`deliver-nearby-${o.id}`}>Reached Nearby</Button>
                    ) : null}
                    {o.ondemand?.status !== "delivered" ? (
                      <Button size="sm" className="bg-[#0C831F] hover:bg-[#0a6e1a]" onClick={() => act(o.id, "delivered")} data-testid={`deliver-done-${o.id}`}>Order delivered</Button>
                    ) : null}
                    <Button size="sm" variant="outline" onClick={() => act(o.id, "reject")} data-testid={`deliver-reject-${o.id}`}>Reject</Button>
                  </>
                )}
              />
            ))}
            {!accepted.length ? <p className="text-sm text-stone-500">Accept an order to start.</p> : null}
          </div>
        </section>
        <section data-testid="deliver-recent-delivered">
          <h2 className="font-extrabold text-lg mb-1">Recent delivered</h2>
          <p className="text-sm text-stone-500 mb-3">Orders you marked as delivered.</p>
          <div className="space-y-3">
            {delivered.map((o) => <OrderCard key={o.id} o={o} tone="done" />)}
            {!delivered.length ? <p className="text-sm text-stone-500">No delivered orders yet.</p> : null}
          </div>
        </section>
        <section data-testid="deliver-rejected">
          <h2 className="font-extrabold text-lg mb-1">Rejected</h2>
          <p className="text-sm text-stone-500 mb-3">Orders you sent back. Another rider can still take them.</p>
          <div className="space-y-3">
            {rejected.map((o) => <OrderCard key={o.id} o={o} tone="rejected" />)}
            {!rejected.length ? <p className="text-sm text-stone-500">No rejected orders.</p> : null}
          </div>
        </section>
      </div>
    </div>
  );
}
